import { useRef, useState } from "react";
import { Alert, Col, Collapse, Row } from "reactstrap";
import MultipleTextOption from "../MultipleText/MultipleTextOption";
import CollapseHeader from "../References/ReferencesComponents/CollapseHeader";
import LocationEditor from "./LocationEditor";
import LocationOptions from "./LocationOptions";
import countries from "i18n-iso-countries";

import * as items from "i18n-iso-countries/langs/en.json";
import LocationHeader from "./LocationHeader";
import StrUtils from "utils/StrUtils";
import classNames from "classnames";
import LocationInputDropdown from "./LocationInputDropdown";
import LocationMap from "./LocationMap";
import pluralize from "pluralize";
import {
  getLocationValue,
  handleLocationSelection,
} from "./LocationHelperFunctions";
import { geocodeByAddress } from "react-places-autocomplete";

countries.registerLocale(items);

export const getIsCountry = (location) => {
  return (
    !location?.Address.Address1 &&
    !location?.Address.Address2 &&
    !location?.Address.City &&
    !location?.Address.StateProvince &&
    !location?.Address.StreetNumber &&
    !location?.Address.ZipCode &&
    location?.Address.CountryCode2
  );
};

export const CountrySymbol = ({ location }) => {
  return (
    <div
      style={{
        width: 20,
        height: 18,
      }}
      className={`d-inline-block flag:${location.Address.CountryCode2} border border-light`}
    ></div>
  );
};

const getInitialLocation = () => ({
  Readable: "",
  Address: {
    Address1: "",
    Address2: "",
    StreetNumber: "",
    City: "",
    StateProvince: "",
    ZipCode: "",
    Country: "",
    CountryCode2: "",
    CountryCode3: "",
  },
  Geo: {
    Latitude: null,
    Longitude: null,
  },
  Description: "",
  Type: null,
  Primary: false,
});

/**
 * Location Object
 *
 * Readable - Readable address string
 * Address - Object containing address keys (Address1, Addres2, StreetNumber, etc.)
 * Geo - Object containing latitude and longitude keys (lat, long)
 * id - The ID of the location (For application CRUD purposes)
 * Type - Object containing Location Type info (label, value, color. Same as Email/Phone/URL fields)
 * Description - User defined description string of the Location
 * Primary - Boolean of whether or not the location is the primary location.
 */

/**
 * Location - Location Editor built for LocationRecord / LocationCreate (But not limited to them)
 *
 * locations - All the location objects that can be edited/viewed in this component
 * setLocations - For updating the list of location objects
 * setOptionsOpen - State setter so parent knows when nested components are open
 * defaultOpen - Whether or not editor should be open by default (ex. CreateMode aka LocationCreate)
 * field - Schema of the location field
 * onClose - Callback function for when component is closed
 */
const Location = ({
  locations,
  fullScreen,
  setFullScreen,
  setOptionsOpen,
  defaultOpen = false,
  field,
  onClose,
  save,
  existingValues,
}) => {
  const [editorOpen, setEditorOpen] = useState(defaultOpen);
  const [isEditing, setIsEditing] = useState(true); // Whether or not the location search input is in edit mode
  const [editLocation, setEditLocation] = useState(getInitialLocation());
  const [searchText, setSearchText] = useState("");
  const [customAddress, setCustomAddress] = useState(""); // Handles Address 1 manual configuration state
  const [error, setError] = useState(null);
  const [processingLocation, setProcessingLocation] = useState(false);
  const [isGoogleLocation, setIsGoogleLocation] = useState(false); // Whether or not Address was selected from google auto complete (Doesn't need to be validated again)

  locations = Array.isArray(locations) ? locations : [];

  return (
    <>
      <LocationHeader
        field={field}
        fullScreen={fullScreen}
        setFullScreen={setFullScreen}
        onClose={onClose}
      />
      {!locations?.length && (
        <div className="px-2 py-1 font-weight-500">
          No {pluralize(field?.Name)} have been added.
        </div>
      )}
      {locations.map((location, i) => {
        const isCountry = getIsCountry(location);

        return (
          <MultipleTextOption
            key={i}
            isLast={true}
            isPrimary={location?.Primary}
            title={location?.Readable}
            type={"Location"}
            onOpen={() => setOptionsOpen(true)}
            onClose={() => setOptionsOpen(false)}
            icon={isCountry ? null : "map-marker"}
            custom={isCountry ? <CountrySymbol location={location} /> : null}
            options={(ref) => (
              <LocationOptions
                onModifyLocation={() => onStartEditingLocation(location, ref)}
                onRemoveLocation={() => onRemoveLocation(location.id, ref)}
                onSetLocationAsPrimary={() =>
                  onSetLocationAsPrimary(location.id, ref)
                }
                location={location}
              />
            )}
            copy={location.Readable}
          />
        );
      })}
      <CollapseHeader
        title={editLocation?.id ? "Editing Location" : "Add New Location"}
        isOpen={editorOpen}
        onClick={() => setEditorOpen((prev) => !prev)}
      />
      <Collapse isOpen={editorOpen}>
        <Row className="mt-2 border-bottom border-superlight">
          <Col xs="12">
            <LocationInputDropdown
              location={editLocation}
              setLocation={setEditLocation}
              onReset={cleanAutocomplete}
              searchText={searchText}
              setSearchText={setSearchText}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              processingLocation={processingLocation}
              isGoogleLocation={isGoogleLocation}
              setIsGoogleLocation={setIsGoogleLocation}
            />
          </Col>
          <Col xs="12">
            {error && (
              <Alert className="py-1 font-weight-500 mb-0" color="danger">
                {error}
              </Alert>
            )}
          </Col>
        </Row>
        <Row>
          <Col
            xs="12"
            md={fullScreen ? "6" : "12"}
            className={classNames({ "pr-0 mx-0": fullScreen })}
          >
            {editorOpen && (
              <LocationMap
                lat={editLocation?.Geo?.lat ?? 37.0902}
                lng={editLocation?.Geo?.lng ?? -95.7129}
                useMapMarker={
                  editLocation?.Geo?.lat && editLocation?.Geo?.lng
                    ? true
                    : false
                }
                zoom={editLocation?.Geo?.lat && editLocation?.Geo?.lng ? 10 : 1}
                height={fullScreen ? "100%" : 200}
              />
            )}
          </Col>
          <Col
            xs="12"
            md={fullScreen ? "6" : "12"}
            className={classNames({
              "border-left pl-0 mx-0": fullScreen,
            })}
          >
            <LocationEditor
              location={editLocation}
              setLocation={setEditLocation}
              setOptionsOpen={setOptionsOpen}
              setEditorOpen={setEditorOpen}
              fullScreen={fullScreen}
              customAddress={customAddress}
              setCustomAddress={setCustomAddress}
              setSearchText={setSearchText}
              setIsEditing={setIsEditing}
              setIsGoogleLocation={setIsGoogleLocation}
              onAddLocation={onAddLocation}
            />
          </Col>
        </Row>
      </Collapse>
    </>
  );

  function resetState() {
    setEditLocation(getInitialLocation());
    setCustomAddress("");
    setSearchText("");
    setIsEditing(true);
    setError(null);
    setIsGoogleLocation(false);
    setEditorOpen(false);
  }

  function cleanAutocomplete() {
    setEditLocation(getInitialLocation());
    setSearchText("");
    setIsEditing(true);
  }

  function onStartEditingLocation(location, ref) {
    setError(null);
    setEditLocation(location);
    setSearchText(location?.Readable);
    setEditorOpen(true);
    setIsEditing(false);
    ref.current.close();
  }

  async function getResultLocation() {
    // If the address was selected from google auto complete, no need to revalidate.
    if (isGoogleLocation) return editLocation;

    setProcessingLocation(true);
    const resultLocation = await locationValidator(editLocation);
    setProcessingLocation(false);

    return resultLocation;
  }

  async function onAddLocation() {
    if (editLocation.hasOwnProperty("id")) {
      return onEditLocation();
    }

    const resultLocation = await getResultLocation(editLocation);

    if (!resultLocation) return;

    for (const loc of locations) {
      if (loc?.Readable === resultLocation?.Readable) {
        return setError("You've already added this location!");
      }
    }

    const newLocation = {
      ...editLocation,
      ...resultLocation,
      id: StrUtils.uuidv4(),
    };

    const existing = Array.isArray(existingValues) ? existingValues : [];

    save([...existing, newLocation]);

    resetState();
  }

  async function onEditLocation() {
    const resultLocation = await getResultLocation(editLocation);

    if (!resultLocation) return;

    for (const loc of locations) {
      if (
        loc.id !== editLocation.id &&
        loc?.Readable === resultLocation?.Readable
      ) {
        return setError("You've already added this location!");
      }
    }

    const updatedLocations = existingValues.map((location) => {
      if (location.id === editLocation.id) {
        return { ...editLocation, ...resultLocation };
      }

      return location;
    });

    save(updatedLocations);
    resetState();
  }

  function onRemoveLocation(locationID, ref) {
    if (editLocation?.id === locationID) {
      setEditLocation(getInitialLocation());
    }

    const updatedLocations = existingValues.filter(
      (location) => location.id !== locationID
    );

    save(updatedLocations);
    ref.current.close();
  }

  function onSetLocationAsPrimary(locationID, ref) {
    ref.current.close();

    const updatedLocations = existingValues?.map((loc) => {
      if (loc.Primary === true && loc.id !== locationID) {
        loc.Primary = false;
      }

      if (loc.id === locationID) {
        loc.Primary = true;
      }

      return loc;
    });

    save(updatedLocations);
  }

  // Validates custom location, updates Object if passes
  async function locationValidator(newLocation) {
    const manualLocationValue = getLocationValue(newLocation.Address);
    let location = null;

    try {
      await geocodeByAddress(manualLocationValue);

      setError(null);
      const newLocation = await handleLocationSelection(manualLocationValue);

      setSearchText(manualLocationValue);
      location = newLocation;
    } catch (err) {
      setError("Invalid location entered!");
    }

    return location;
  }
};

export default Location;
